import { useQuery } from "@tanstack/react-query";
import { Star, MapPin } from "lucide-react";
import api from "@/lib/api";

const Stars = ({ n }) => (
  <span className="inline-flex" aria-label={`${n} stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={14} className={i <= Math.round(n || 0) ? "fill-[#E8B04B] text-[#E8B04B]" : "text-[#D8D2C7]"} />
    ))}
  </span>
);

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/><path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/></svg>
);

export default function GoogleReviews({ title = "What people say on Google", compact = false }) {
  const { data } = useQuery({ queryKey: ["google-reviews"], queryFn: () => api.get("/google-reviews").then((r) => r.data) });
  const reviews = (data?.reviews || []).filter((r) => r.text);
  if (!reviews.length) return null;
  const shown = compact ? reviews.slice(0, 3) : reviews;

  return (
    <section data-testid="google-reviews" className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <GoogleG />
          <div>
            <h3 className="font-serif text-2xl text-[#1A242B]">{title}</h3>
            {data?.average_rating && (
              <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Stars n={data.average_rating} /> {data.average_rating} · {data.total_rating_count} reviews on Google</p>
            )}
          </div>
        </div>
        <span className="text-xs text-[#8FA89B] inline-flex items-center gap-1.5"><GoogleG /> Reviews via Google</span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {shown.map((r, i) => (
          <article key={i} data-testid={`google-review-${i}`} className="eyw-card p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-3">
              {r.author_photo
                ? <img src={r.author_photo} alt={r.author_name} className="h-9 w-9 rounded-full" referrerPolicy="no-referrer" />
                : <span className="h-9 w-9 rounded-full bg-[#144F75] text-white grid place-items-center text-sm">{(r.author_name || "G").charAt(0)}</span>}
              <div className="min-w-0">
                {r.author_url
                  ? <a href={r.author_url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#1A242B] hover:underline truncate block">{r.author_name}</a>
                  : <span className="text-sm font-medium text-[#1A242B] truncate block">{r.author_name}</span>}
                <span className="text-xs text-[#8FA89B]">{r.relative_time}</span>
              </div>
              <span className="ml-auto"><GoogleG /></span>
            </div>
            <Stars n={r.rating} />
            <p className="text-sm text-[#4A5D6B] mt-2 flex-1 line-clamp-6">{r.text}</p>
            {r.place_label && <p className="text-xs text-[#8FA89B] mt-3 flex items-center gap-1"><MapPin size={11} /> {r.place_label}</p>}
          </article>
        ))}
      </div>
    </section>
  );
}
